import React, { useEffect, useState } from 'react'
import {
  MotifButton,
  MotifIcon,
  MotifFormField,
} from '@ey-xd/motif-react'
import { useParams } from 'react-router-dom'
import Table from '@/components/Table/table'
import { useColumnDeveloperEvidenceListDefs } from './Constants'
import './DeveloperEvidence.scss'
import { customTableLoadingData } from '@/components/Table/CustomPaginationConstants'
import UploadEvidence from './UploadEvidence'
import { useListEvidenceQuery } from '@/services/application/evidenceReducer'
import { IconoirUpload, IconoirWarningTriangle } from '@ey-xd/motif-icon'
import Annotation from '../Annotation/Annotation'
import UploadSupplymentEvidence from './UploadSupplymentEvidence'
import { useDispatch, useSelector } from 'react-redux'
import { EvidenceStatusEnum } from '@/services/types'
import { setSupplementEvidence } from './DeveloperEvidenceSlice'

type EvidenceType = {
  evidenceType: number
}

export default function DeveloperEvidence(props: EvidenceType): JSX.Element {
  const tableLoadingData = customTableLoadingData
  const evidenceType = props?.evidenceType || 1
  const [showModal, setShowModal] = useState(false)
  const [skippedList, setSkippedList] = useState<string[]>([])
  const [canSkip, setCanSkip] = useState(false)
  const dispatch = useDispatch()
  const { engagementId, applicationId } = useParams<{
    engagementId: string
    applicationId: string
  }>()

  const {
    data: evidenceList,
    error,
    refetch: listEvidence,
    isLoading,
  } = useListEvidenceQuery(
    { applicationId, evidenceType: evidenceType },
    { refetchOnMountOrArgChange: true },
  )
  
  const devEvidenceState = useSelector((state: any) => state.DeveloperEvidence)
 
  useEffect(() => {
    if (!showModal) {
      listEvidence()
    }
  }, [showModal])

  useEffect(() => {
    if (
      !devEvidenceState.isModalOpen &&
      (devEvidenceState.isSupplymentUploaded || devEvidenceState.isRefreshRequired)
    ) {
      listEvidence()
    }
  }, [devEvidenceState.isModalOpen, devEvidenceState.isSupplymentUploaded, devEvidenceState.isRefreshRequired, listEvidence])

  useEffect(() => {
    if (typeof evidenceList === 'undefined') return
    if (evidenceList.some((e: { evidenceStatus: number }) => e.evidenceStatus === EvidenceStatusEnum.MalwareScan)) {
        const timer = setInterval(() => {
          listEvidence()

          if (!evidenceList.some((e: { evidenceStatus: number }) => e.evidenceStatus === EvidenceStatusEnum.MalwareScan)) {
            clearInterval(timer)
          }
        }, 3000)

        return () => clearInterval(timer)
    }
  }, [evidenceList, listEvidence])

  useEffect(() => {
    if (typeof evidenceList === 'undefined') return
    // can skip if devEvidenceState.evidenceGroupId is defined and has more that one system account in evidence list matching the groupId
    const hasMultipleSystemAccounts = evidenceList.some((e: { systemAccounts: any[]; evidenceGroupId: string }) =>
      e.evidenceGroupId === devEvidenceState.evidenceGroupId && e.systemAccounts.length > 1
    )
    setCanSkip(hasMultipleSystemAccounts)
  }, [devEvidenceState.evidenceGroupId, evidenceList])   

  const handleSystemAccountSkip = () => {
    const skipped = skippedList;
    if (!devEvidenceState.supplementAccountId) return;
    if (!skipped.includes(devEvidenceState.supplementAccountId)) {
      skipped.push(devEvidenceState.supplementAccountId);
      setSkippedList(skipped);
    }

    evidenceList.filter((e => e.evidenceGroupId === devEvidenceState.evidenceGroupId)).forEach((e) => {
      if (e.systemAccounts && e.systemAccounts.length > 0) {
        const systemAccount = e.systemAccounts.find((sa: { id: string }) => !skippedList.includes(sa.id))
        if (systemAccount) {
          dispatch(
            setSupplementEvidence({
              isSupplymentRequired: true,
              evidenceGroupId: e.evidenceGroupId,
              isSupplymentUploaded: false,
              supplementAccountName: systemAccount.accountName,
              supplementAccountId: systemAccount.id,
            }),
          )
        } else {
          dispatch(
            setSupplementEvidence({
              isSupplymentRequired: false,
              evidenceGroupId: "",
              isSupplymentUploaded: false,
              supplementAccountName: undefined,
              supplementAccountId: undefined,
            }),
          )
          setSkippedList([])
        }
      }
    })
  }

  const unverifiedCount =
    evidenceList?.filter((e: { evidenceStatus: number; hasAnnotations?: boolean }) => e.evidenceStatus !== EvidenceStatusEnum.Verified && e.hasAnnotations).length ?? 0

  return (
    <>
      {unverifiedCount > 0 && (
        <div className="unverified-evidence-banner" role="alert">
          <MotifIcon src={IconoirWarningTriangle} size="16" fill="#D07200" stroke="#D07200" aria-hidden="true" />
          <span>
            {unverifiedCount} unverified evidence file{unverifiedCount !== 1 ? 's' : ''}. You may navigate to other steps but appropriateness will not begin until all evidence files are verified.
          </span>
        </div>
      )}
      <Table
        columnDefs={useColumnDeveloperEvidenceListDefs()}
        cellRenderers={evidenceList}
        TitleComplement={() => (
          <MotifFormField>
            <MotifButton
              className="motif-button upload-btn"
              size="small"
              onClick={() => setShowModal(true)}
            >
              <MotifIcon
                iconFunction={IconoirUpload}
                title="Upload HR document"
              />
              <span>Upload</span>
            </MotifButton>
          </MotifFormField>
        )}
        title={
          evidenceType === 1
            ? 'Upload developer evidence'
            : 'Upload implementor evidence'
        }
        description={
          "Once you've uploaded the" +
          (evidenceType === 1 ? ' developer' : ' implementor') +
          '  evidence, please review and verify the information extracted.'
        }
        pagination={true}
        overlayNoRowsTemplate={
          isLoading
            ? tableLoadingData
            : error
              ? 'Something went wrong!'
              : 'No records found'
        }
      />
      <UploadEvidence
        isVisible={showModal}
        setIsVisible={setShowModal}
        setUploadDocId={() => {}}
        evidenceType={evidenceType}
      />
      <UploadSupplymentEvidence
        isVisible={devEvidenceState.isSupplymentRequired}
        setIsVisible={() => {}}
        groupId={devEvidenceState.evidenceGroupId}
        supplementAccountName={devEvidenceState.supplementAccountName}
        supplementAccountId={devEvidenceState.supplementAccountId}
        canSkip={canSkip}
        handleSkip={handleSystemAccountSkip}
      />
      <Annotation
        isVisible={devEvidenceState.isModalOpen}
        fileName={devEvidenceState.fileName}
        evidenceId={devEvidenceState.evidenceId}
        refetchEvidenceList={listEvidence}
      />
    </>
  )
}
